import { extractMessage, isSuccess, post } from '../../services/api';

function normalizeOrder(item) {
  if (!item || typeof item !== 'object') {
    return null;
  }

  return {
    ...item,
    id: Number(item.id || 0),
    club_id: Number(item.club_id || 0),
    seat_id: item.seat_id ? Number(item.seat_id) : null,
    guest_count: Number(item.guest_count || 1),
    total_price:
      item.total_price === null || item.total_price === undefined
        ? null
        : Number(item.total_price),
    status: item.status || 'created',
    payment_status: item.payment_status || 'not_paid',
    club_timezone: item.club_timezone || item.timezone || item.club?.timezone || '',
    files: Array.isArray(item.files) ? item.files : [],
  };
}

function normalizeOrderList(items) {
  if (!Array.isArray(items)) return [];
  return items.map(normalizeOrder).filter(Boolean);
}

const defaultStats = () => ({
  total: 0,
  created: 0,
  confirmed: 0,
  in_progress: 0,
  completed: 0,
  cancelled: 0,
  no_show: 0,
});

export default {
  namespaced: true,

  state: () => ({
    items: [],
    current: null,
    loadingList: false,
    loadingCurrent: false,
    creating: false,
    loadingStats: false,
    updatingStatus: false,
    error: '',
    offset: 0,
    hasMore: true,
    total: 0,
    stats: defaultStats(),
  }),

  getters: {
    items: (state) => state.items,
    current: (state) => state.current,
    loadingList: (state) => state.loadingList,
    loadingCurrent: (state) => state.loadingCurrent,
    creating: (state) => state.creating,
    createLoading: (state) => state.creating,
    loadingStats: (state) => state.loadingStats,
    updatingStatus: (state) => state.updatingStatus,
    error: (state) => state.error,
    hasMore: (state) => state.hasMore,
    total: (state) => state.total,
    stats: (state) => state.stats,
  },

  mutations: {
    SET_ERROR(state, value) {
      state.error = value;
    },
    SET_LIST_LOADING(state, value) {
      state.loadingList = value;
    },
    SET_CURRENT_LOADING(state, value) {
      state.loadingCurrent = value;
    },
    SET_CREATING(state, value) {
      state.creating = value;
    },
    SET_CREATE_LOADING(state, value) {
      state.creating = value;
    },
    SET_STATS_LOADING(state, value) {
      state.loadingStats = value;
    },
    SET_STATUS_LOADING(state, value) {
      state.updatingStatus = value;
    },
    SET_ITEMS(state, items) {
      state.items = normalizeOrderList(items);
    },
    APPEND_ITEMS(state, items) {
      const safeItems = normalizeOrderList(items);
      const known = new Set(state.items.filter(Boolean).map((item) => item.order_number));

      safeItems.forEach((item) => {
        if (!item?.order_number) return;

        if (!known.has(item.order_number)) {
          state.items.push(item);
          known.add(item.order_number);
        }
      });
    },
    PREPEND_ORDER(state, order) {
      const normalized = normalizeOrder(order);
      if (!normalized?.order_number) return;

      const index = state.items.findIndex(
        (item) => item?.order_number === normalized.order_number
      );

      if (index === -1) {
        state.items.unshift(normalized);
      } else {
        state.items.splice(index, 1, normalized);
      }

      state.current = normalized;
    },
    UPSERT_ORDER(state, order) {
      const normalized = normalizeOrder(order);
      if (!normalized?.order_number) return;

      const index = state.items.findIndex(
        (item) => item?.order_number === normalized.order_number
      );

      if (index === -1) {
        state.items.unshift(normalized);
      } else {
        state.items.splice(index, 1, normalized);
      }

      state.current = normalized;
    },
    SET_CURRENT(state, order) {
      state.current = normalizeOrder(order);
    },
    SET_PAGINATION(state, payload) {
      state.offset = Number(payload?.next_offset || 0);
      state.hasMore = Boolean(payload?.has_more);
      state.total = Number(payload?.total || 0);
    },
    SET_STATS(state, payload) {
      state.stats = {
        ...defaultStats(),
        ...Object.fromEntries(
          Object.entries(payload || {}).map(([key, value]) => [key, Number(value || 0)])
        ),
      };
    },
    RESET_STATE(state) {
      state.items = [];
      state.current = null;
      state.loadingList = false;
      state.loadingCurrent = false;
      state.creating = false;
      state.loadingStats = false;
      state.updatingStatus = false;
      state.error = '';
      state.offset = 0;
      state.hasMore = true;
      state.total = 0;
      state.stats = defaultStats();
    },
  },

  actions: {
    resetState({ commit }) {
      commit('RESET_STATE');
    },

    clearOrdersCache({ commit }) {
      commit('RESET_STATE');
    },

    async createBooking({ commit, rootGetters }, payload) {
      commit('SET_CREATE_LOADING', true);
      commit('SET_ERROR', '');

      try {
        const user = rootGetters['auth/user'];

        if (!user?.hash) {
          const message = 'Пользователь не авторизован';
          commit('SET_ERROR', message);
          return { ok: false, message };
        }

        const result = await post('/app/orders/create', {
          hash: user.hash,
          club_id: payload.club_id,
          seat_id: payload.seat_id,
          booking_type: payload.booking_type,
          zone_name: payload.zone_name || '',
          seat_label: payload.seat_label || '',
          tariff_name: payload.tariff_name || '',
          hours_count: payload.hours_count || null,
          booking_date: payload.booking_date,
          start_time: payload.start_time,
          end_time: payload.end_time || null,
          guest_count: payload.guest_count || 1,
          comment: payload.comment || '',
          payment_status: payload.payment_status || 'not_paid',
          payment_method: payload.payment_method || 'card',
          total_price: payload.total_price || 0,
          status: payload.status || 'created',
          source: payload.source || 'app',
          promo_code: payload.promo_code || '',
        });

        if (!isSuccess(result)) {
          const message = extractMessage(result, 'Не удалось создать бронь');
          commit('SET_ERROR', message);
          return { ok: false, message };
        }

        const rawOrder = result?.data?.order || result?.data || null;
        const order = normalizeOrder(rawOrder);

        if (order?.order_number) {
          commit('PREPEND_ORDER', order);
        }

        return { ok: true, order };
      } catch (error) {
        const message = 'Ошибка сети';
        commit('SET_ERROR', message);
        return { ok: false, message };
      } finally {
        commit('SET_CREATE_LOADING', false);
      }
    },

    async createOrder(context, payload) {
      return context.dispatch('createBooking', payload);
    },

    async loadOrders({ commit, state, rootGetters }, { reset = false, filters = {} } = {}) {
      const user = rootGetters['auth/user'];
      const isAdmin = rootGetters['auth/isAdmin'];

      if (!user?.hash) return { ok: false, message: 'Пользователь не авторизован' };
      if (state.loadingList) return { ok: false };
      if (!reset && !state.hasMore) return { ok: true, noMore: true };

      commit('SET_LIST_LOADING', true);
      commit('SET_ERROR', '');

      try {
        const endpoint = isAdmin ? '/app/orders/admin/list' : '/app/orders/list';
        const payload = {
          hash: user.hash,
          limit: isAdmin ? 12 : 8,
          offset: reset ? 0 : state.offset,
          ...filters,
        };

        const result = await post(endpoint, payload);

        if (!isSuccess(result)) {
          const message = extractMessage(result, 'Не удалось загрузить брони');
          commit('SET_ERROR', message);
          return { ok: false, message };
        }

        const data = result?.data || {};
        const items = normalizeOrderList(data?.items);

        if (reset) {
          commit('SET_ITEMS', items);
        } else {
          commit('APPEND_ITEMS', items);
        }

        commit('SET_PAGINATION', data);
        return { ok: true, items };
      } catch (error) {
        commit('SET_ERROR', 'Ошибка сети');
        return { ok: false, message: 'Ошибка сети' };
      } finally {
        commit('SET_LIST_LOADING', false);
      }
    },

    async fetchOrder({ commit, rootGetters }, orderNumber) {
      const user = rootGetters['auth/user'];
      const isAdmin = rootGetters['auth/isAdmin'];

      if (!user?.hash) return { ok: false, message: 'Пользователь не авторизован' };

      commit('SET_CURRENT_LOADING', true);
      commit('SET_ERROR', '');

      try {
        const endpoint = isAdmin ? '/app/orders/admin/get' : '/app/orders/get';
        const result = await post(endpoint, {
          hash: user.hash,
          order_number: orderNumber,
        });

        if (!isSuccess(result)) {
          const message = extractMessage(result, 'Не удалось получить бронь');
          commit('SET_ERROR', message);
          return { ok: false, message };
        }

        const rawOrder = result?.data?.order || result?.data || null;
        const order = normalizeOrder(rawOrder);

        if (!order) {
          const message = 'Не удалось получить бронь';
          commit('SET_ERROR', message);
          commit('SET_CURRENT', null);
          return { ok: false, message };
        }

        commit('UPSERT_ORDER', order);
        return { ok: true, order };
      } catch (error) {
        commit('SET_ERROR', 'Ошибка сети');
        return { ok: false, message: 'Ошибка сети' };
      } finally {
        commit('SET_CURRENT_LOADING', false);
      }
    },

    async fetchStatistics({ commit, rootGetters }, filters = {}) {
      const user = rootGetters['auth/user'];

      if (!user?.hash || !rootGetters['auth/isAdmin']) return null;

      commit('SET_STATS_LOADING', true);

      try {
        const result = await post('/app/orders/admin/stats', {
          hash: user.hash,
          ...filters,
        });

        if (isSuccess(result)) {
          commit('SET_STATS', result.data || {});
          return result.data;
        }

        commit('SET_ERROR', extractMessage(result, 'Не удалось получить статистику'));
        return null;
      } catch (error) {
        commit('SET_ERROR', 'Ошибка сети');
        return null;
      } finally {
        commit('SET_STATS_LOADING', false);
      }
    },

    async updateStatus({ commit, rootGetters }, payload) {
      const user = rootGetters['auth/user'];

      if (!user?.hash || !rootGetters['auth/isAdmin']) {
        return { ok: false, message: 'Недостаточно прав' };
      }

      commit('SET_STATUS_LOADING', true);
      commit('SET_ERROR', '');

      try {
        const result = await post('/app/orders/admin/update-status', {
          hash: user.hash,
          ...payload,
        });

        if (!isSuccess(result)) {
          const message = extractMessage(result, 'Не удалось обновить статус');
          commit('SET_ERROR', message);
          return { ok: false, message };
        }

        const rawOrder = result?.data?.order || result?.data || null;
        const order = normalizeOrder(rawOrder);

        if (!order) {
          const message = 'Не удалось обновить статус';
          commit('SET_ERROR', message);
          return { ok: false, message };
        }

        commit('UPSERT_ORDER', order);
        return { ok: true, order };
      } catch (error) {
        commit('SET_ERROR', 'Ошибка сети');
        return { ok: false, message: 'Ошибка сети' };
      } finally {
        commit('SET_STATUS_LOADING', false);
      }
    },

    clearError({ commit }) {
      commit('SET_ERROR', '');
    },
  },
};